/**
 * PRD.247 — Billing Item Payment Status table (item-level settlement summary).
 *
 * Billing is the primary surface for "which item is paid / partially paid /
 * still outstanding?". For each invoice item it shows Charge / Paid /
 * Outstanding / Status, derived strictly from REAL payment allocations
 * (invoice_payments.allocations):
 *   - Item paid = the amount explicitly allocated to that item.
 *   - Unallocated invoice-level payments are NOT spread across items — doing so
 *     would falsely imply item-level settlement that the data doesn't support
 *     (PRD.247 §5.5 / §7.2). Instead a lightweight notice flags that some
 *     payment isn't item-allocated.
 *
 * Invoice-level totals are unaffected — they always sum amount_cents — so this
 * panel is purely a status view (PRD.247 §7.4). Builds on PRD.244.
 */

import { useMemo } from 'react';
import { usePowerSyncQuery } from '../../adapters/powersync';
import {
  qInvoiceItems,
  qInvoicePaymentsByInvoice,
  aggregatePaymentAllocations,
  type InvoiceItemRow,
  type InvoicePaymentRow,
} from '@helios/data-access';
import { phpFromCents } from '@helios/domain';
import { useFormatMoney } from '../../hooks/useFormatMoney';
import { itemChargeCents } from './payment-allocation-utils';

interface Props {
  invoiceId: string;
}

type PaymentStatus = 'paid' | 'partial' | 'unpaid';

interface ItemStatus {
  id: string;
  name: string;
  chargeCents: number;
  paidCents: number;
  outstandingCents: number;
  status: PaymentStatus;
}

function statusOf(chargeCents: number, paidCents: number): PaymentStatus {
  // A zero-charge line has nothing owed → settled.
  if (chargeCents <= 0 || paidCents >= chargeCents) return 'paid';
  if (paidCents > 0) return 'partial';
  return 'unpaid';
}

const STATUS_STYLE: Record<PaymentStatus, { label: string; color: string; bg: string }> = {
  paid: {
    label: 'Paid',
    color: 'var(--color-success-text, #15803d)',
    bg: 'var(--color-success-bg, #f0fdf4)',
  },
  partial: {
    label: 'Partial',
    color: 'var(--color-warning-text, #92400e)',
    bg: 'var(--color-warning-bg, #fff7ed)',
  },
  unpaid: {
    label: 'Unpaid',
    color: 'var(--text-muted, #6b7280)',
    bg: 'var(--bg-surface-2, #f3f4f6)',
  },
};

export function InvoiceItemPaymentStatus({ invoiceId }: Props) {
  const fmt = useFormatMoney();
  const { data: items = [] } = usePowerSyncQuery<InvoiceItemRow>(qInvoiceItems(invoiceId));
  const { data: payments = [] } = usePowerSyncQuery<InvoicePaymentRow>(
    qInvoicePaymentsByInvoice(invoiceId),
  );

  const { rows, unallocatedCents } = useMemo(() => {
    const knownIds = new Set(items.map((i) => i.id));
    // Item paid comes ONLY from explicit allocations — never from spreading
    // unallocated payments (PRD.247 §5.5)...
    const { allocatedByItem, totalPaidCents, unallocatedCents } = aggregatePaymentAllocations(
      payments,
      knownIds,
    );

    // ...except a fully-settled invoice (paid ≥ total charge) unambiguously
    // settles every item on it — a reliable rule, detected by AMOUNT so a stale
    // status flag can't mislead.
    const invoiceChargeCents = items.reduce((s, it) => s + itemChargeCents(it), 0);
    const invoiceFullyPaid = invoiceChargeCents > 0 && totalPaidCents >= invoiceChargeCents;

    const rows: ItemStatus[] = items.map((it) => {
      const chargeCents = itemChargeCents(it);
      const paidCents = invoiceFullyPaid
        ? chargeCents
        : Math.min(chargeCents, allocatedByItem.get(it.id) ?? 0);
      return {
        id: it.id,
        name: it.service_name || it.description_snapshot || it.free_text_name || 'Item',
        chargeCents,
        paidCents,
        outstandingCents: Math.max(0, chargeCents - paidCents),
        status: statusOf(chargeCents, paidCents),
      };
    });
    // When the invoice is fully paid, allocation no longer matters — suppress
    // the unallocated notice so it doesn't contradict the all-Paid rows.
    return { rows, unallocatedCents: invoiceFullyPaid ? 0 : unallocatedCents };
  }, [items, payments]);

  // No line items → nothing to summarize.
  if (rows.length === 0) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-4) var(--sp-5)',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-3)' }}>
        <h3
          style={{
            margin: 0,
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          Item Payment Status
        </h3>
        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          Paid / outstanding per item
        </span>
      </div>

      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
        }}
      >
        <table
          style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
        >
          <thead>
            <tr style={{ background: 'var(--bg-muted, #f9fafb)' }}>
              <Th>Item</Th>
              <Th align="right">Charge</Th>
              <Th align="right">Paid</Th>
              <Th align="right">Outstanding</Th>
              <Th align="center">Status</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const settled = r.status === 'paid';
              const s = STATUS_STYLE[r.status];
              return (
                <tr key={r.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <Td>{r.name}</Td>
                  <Td align="right" mono>
                    {fmt(phpFromCents(r.chargeCents))}
                  </Td>
                  <Td align="right" mono>
                    <span style={{ color: 'var(--color-success-text)' }}>
                      {fmt(phpFromCents(r.paidCents))}
                    </span>
                  </Td>
                  <Td align="right" mono>
                    <span
                      style={{
                        color: settled ? 'var(--color-success-text)' : 'var(--color-warning-text)',
                        fontWeight: 600,
                      }}
                    >
                      {fmt(phpFromCents(r.outstandingCents))}
                    </span>
                  </Td>
                  <Td align="center">
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 'var(--font-weight-semi)',
                        color: s.color,
                        background: s.bg,
                      }}
                    >
                      {s.label}
                    </span>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {unallocatedCents > 0 && (
        <p style={{ margin: 0, fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
          {fmt(phpFromCents(unallocatedCents))} in payments isn’t allocated to specific items, so it
          isn’t reflected per item above. Edit a payment above (✎) to allocate it to items. Invoice
          totals remain authoritative.
        </p>
      )}
    </div>
  );
}

// ─── Cell helpers ───

function Th({ children, align }: { children: React.ReactNode; align?: 'right' | 'center' }) {
  return (
    <th
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-2) var(--sp-3)',
        fontWeight: 600,
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-xs)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  mono,
}: {
  children: React.ReactNode;
  align?: 'right' | 'center';
  mono?: boolean;
}) {
  return (
    <td
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-2) var(--sp-3)',
        color: 'var(--text)',
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
      }}
    >
      {children}
    </td>
  );
}
